import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { type RefObject, useMemo } from 'react'
import type { GaFormHandle } from '@/features/analytics/gaForm/GaForm'
import { useDeferredGaSubmit } from '@/features/analytics/gaForm/useDeferredGaSubmit'
import type { usePerformMediaBulkEditMutation } from '@/features/bulk-edit/api'
import useUrlParams from '@/hooks/useUrlParams'
import { useAppSelector } from '@/redux/hooks'
import { selectActiveValuesForSubmission } from '../../selectors'
import ChangesTable from './ChangesTable'
import SubmitStatus from './SubmitStatus'

interface Props {
  open: boolean
  onClose: () => void
  gaFormRef: RefObject<GaFormHandle>
  submit: ReturnType<typeof usePerformMediaBulkEditMutation>[0]
  submitStatus: ReturnType<typeof usePerformMediaBulkEditMutation>[1]
}

export default function ReviewDialog(props: Props) {
  const { open, onClose, gaFormRef, submit, submitStatus } = props
  let { mediaNumbers, mediaType } = useUrlParams()
  if (!Array.isArray(mediaNumbers)) mediaNumbers = [mediaNumbers]

  const values = useAppSelector(selectActiveValuesForSubmission())
  const { markPending } = useDeferredGaSubmit(gaFormRef, {
    isFetching: submitStatus.isLoading,
    isSuccess: submitStatus.isSuccess,
    isError: submitStatus.isError,
  })

  const fancyMediaCodeList = useMemo(() => {
    if (mediaNumbers.length === 1) return mediaNumbers
    if (Array.isArray(mediaNumbers))
      return [
        mediaNumbers.slice(0, -1).join(', '),
        mediaNumbers.slice(-1)[0],
      ].join(' and ')
  }, [mediaNumbers])

  function handleClose() {
    submitStatus.reset()
    onClose()
  }

  function handleDone() {
    window.close()
  }

  function handleSubmit() {
    gaFormRef.current?.requestSubmit()
    markPending()
    submit({ mediaNumbers: mediaNumbers, mediaType, ...values })
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll="body"
      fullWidth
      maxWidth="xl"
    >
      <DialogTitle>Review Changes</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Box>
            <Typography>
              Changes will affect {mediaNumbers.length}{' '}
              {mediaNumbers.length > 1 ? 'documents' : 'document'}:
            </Typography>
            <Typography>{fancyMediaCodeList}</Typography>
          </Box>
          <Stack direction="row" justifyContent="space-around" gap={3}>
            <Stack gap={1} flexGrow={1} width="50%">
              <Typography>
                Fields below will be <strong>appended</strong> to all documents.
              </Typography>
              <Paper elevation={2}>
                <ChangesTable actionType="append" />
              </Paper>
            </Stack>
            <Stack gap={1} flexGrow={1} width="50%">
              <Typography>
                Fields below will be <strong>replaced</strong> on all documents.
              </Typography>
              <Paper elevation={2}>
                <ChangesTable actionType="replace" />
              </Paper>
            </Stack>
          </Stack>
          <Typography>
            Please verify that the changes above are correct as this action
            cannot be undone.
          </Typography>
          <SubmitStatus {...submitStatus} />
        </Stack>
      </DialogContent>
      <DialogActions>
        {submitStatus.isSuccess ? (
          <>
            <Button onClick={handleClose} color="info">
              Make More Changes
            </Button>
            <Button variant="contained" onClick={handleDone}>
              Close Window
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="contained"
              onClick={handleClose}
              disabled={submitStatus.isLoading}
              color="secondary"
            >
              Cancel
            </Button>
            <Button
              disabled={submitStatus.isLoading}
              variant="contained"
              onClick={handleSubmit}
              color="primary"
            >
              Submit
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
